import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { AlertTriangle, ArrowRight, UserCircle } from "lucide-react";

/**
 * v8 · Aviso discreto: aparece si el diagnóstico del perfil dice «Arregla esto antes de prospectar».
 * v26g · También invita a quien aún no ha revisado su perfil (sin Instagram vinculado), hasta que lo haga una vez.
 * No bloquea nada; lleva a «Mi perfil en redes».
 */
export default function ProfileNudge({ testId = "profile-nudge", showUnlinked = true }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/instagram/link").then((r) => alive && setInfo(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!info) return null;

  if (!info.linked) {
    if (!showUnlinked) return null;
    return (
      <Link
        to="/app/canales"
        data-testid={`${testId}-unlinked`}
        className="flex items-center justify-between gap-3 rounded-lg border border-brand-accent/30 bg-brand-accent/[0.05] px-4 py-3 text-sm transition-colors hover:bg-brand-accent/[0.09]"
      >
        <span className="flex items-center gap-2.5 text-brand-ink">
          <UserCircle size={16} className="shrink-0 text-brand-accent" />
          <span>Antes de escribir a gente nueva, revisa tu perfil de Instagram: es lo primero que mirarán</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-accent">Revisar mi perfil <ArrowRight size={13} /></span>
      </Link>
    );
  }

  if (info.level_key !== "arregla") return null;
  if (info.tasks_pending === 0) {
    // v26j: ya marcó los arreglos como hechos; no se le sigue diciendo en rojo que arregle su perfil
    return (
      <Link
        to="/app/canales"
        data-testid={`${testId}-done`}
        className="flex items-center justify-between gap-3 rounded-lg border border-brand-accent/30 bg-brand-accent/[0.05] px-4 py-3 text-sm transition-colors hover:bg-brand-accent/[0.09]"
      >
        <span className="flex items-center gap-2.5 text-brand-ink">
          <UserCircle size={16} className="shrink-0 text-brand-accent" />
          <span>Has marcado los arreglos de tu perfil como hechos. Actualiza el diagnóstico para ver tu nueva puntuación</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-accent">Actualizar <ArrowRight size={13} /></span>
      </Link>
    );
  }
  return (
    <Link
      to="/app/canales"
      data-testid={testId}
      className="flex items-center justify-between gap-3 rounded-lg border border-brand-neg/30 bg-brand-neg/[0.05] px-4 py-3 text-sm transition-colors hover:bg-brand-neg/[0.09]"
    >
      <span className="flex items-center gap-2.5 text-brand-ink">
        <AlertTriangle size={16} className="shrink-0 text-brand-neg" />
        <span>
          Tu perfil de Instagram necesita unos arreglos antes de escribir a gente nueva
          {typeof info.score === "number" ? <span className="text-brand-muted"> · {info.score}/100</span> : null}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-accent">Ver qué arreglar <ArrowRight size={13} /></span>
    </Link>
  );
}
